import { useState } from "react";
import { Box, IconButton, Typography, Tooltip } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import { TouchTooltip } from "@/components/TouchTooltip";
import { CreateMenu } from "@/components/CreateMenu";

/**
 * Sidebar Create entry point. Action list lives in `CreateMenu` so the
 * same actions are reused by the AccountsPage header (+) and mobile FAB.
 */

interface Props {
  collapsed: boolean;
}

export function CreateButton({ collapsed }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = !!anchor;

  const trigger = (
    <IconButton
      onClick={(e) => setAnchor(e.currentTarget)}
      aria-label="Create"
      sx={(theme) => ({
        width: 44,
        height: 44,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        boxShadow: "0 6px 16px -4px rgba(38,211,154,0.45)",
        "&:hover": {
          bgcolor: "primary.light",
          boxShadow: "0 8px 20px -4px rgba(38,211,154,0.55)"
        },
        "&:active": { transform: "scale(0.96)" },
        transition: "background-color .15s, box-shadow .15s, transform .1s",
        // When open, subtle ring
        ...(open && {
          outline: `2px solid ${theme.palette.primary.light}`,
          outlineOffset: 2
        })
      })}
    >
      <AddRounded />
    </IconButton>
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: collapsed ? "column" : "row",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: collapsed ? 0.5 : 1.5,
          py: 1.25,
          px: collapsed ? 0 : 1,
          mb: 1.5
        }}
      >
        {collapsed ? (
          <Tooltip title="Create" placement="right">{trigger}</Tooltip>
        ) : (
          <TouchTooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Create</Typography>
                <Typography variant="caption" color="text.secondary">
                  New order, account, bot, list, or screener.
                </Typography>
              </Box>
            }
            placement="right"
            arrow
          >
            {trigger}
          </TouchTooltip>
        )}

        {!collapsed && (
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              cursor: "pointer",
              userSelect: "none"
            }}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            Create
          </Typography>
        )}

        {collapsed && (
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.625rem",
              fontWeight: 700,
              color: "text.secondary",
              letterSpacing: "0.04em"
            }}
          >
            CREATE
          </Typography>
        )}
      </Box>

      {/* Sidebar menu anchors to the right of the button so it doesn't
       *  cover the trigger — matches the Google-Ads sidebar pattern. */}
      <CreateMenu
        anchor={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
      />
    </>
  );
}
